<template>
    <main
        :class="[`ps-side-view ps-${side} patent-app-view__${selfName}`]"
        :data-neighbor-name="neighborName"
        flex="|"
    >
        <ResizeObserver @notify="resizeHandler" />

        <div class="ps-side-view__tab">
            <MenuTabsScrollable
                ref="menuTabsScrollable"
                :currentTab="selfName"
                :defaultTabs="defaultTabs"
                :showTooltip="true"
                @switchTabHanlder="switchTabHandler"
            />
        </div>

        <router-view
            :key="`${side}_${selfName}`"
            ref="viewVm"
            :class="['dual-aside-box', dualViewClassStatus]"
            :name="selfName"
            :side="side"
            :selfName="selfName"
            :neighborSide="neighborSide"
            :neighborName="neighborName"
            :patentId="viewParams.patentId"
            flex-item="()"
        />

        <DensityBar v-if="hasDensity" :key="selfName" />
    </main>
</template>

<script>
/*
     ⚠️ 此组件存在的目的仅仅是为了解析左右路由关系 ⚠️
        ⚠️ 请把此组件当成 Functional 组件使用！⚠️
                     🙏
*/
import DensityBar from 'library-@/components/DensityBar';
import _ from 'lodash';
import MenuTabsScrollable from 'library-@/components/MenuTabs/MenuTabsScrollable';
import { getFilteredManifestList, pluginsMapByName } from '@/subview_plugins/install';
import { getRouteNameList } from '@/router';
import { ResizeObserver } from 'vue-resize';
import { getDualViewDisplayTabBySide } from '@/utils/dual-view-setting';
import { useDualViewStates } from '@/composables/use-dual-view-states';
import { usePermission } from '@/composables/use-permission';

export default {
    name: 'SideView',
    components: {
        MenuTabsScrollable,
        DensityBar,
        ResizeObserver,
    },
    props: {
        viewParams: {
            type: Object,
            required: true,
        },
        neighborName: {
            type: String,
        },
        selfName: {
            type: String,
        },
        neighborSide: {
            type: String, // 'left' | 'right'
            required: true,
        },
        side: {
            type: String, // 'left' | 'right'
            required: true,
        },
    },
    setup() {
        const { dualViewSetting, updateDualViewRef, clearDualViewRef } = useDualViewStates();

        const { permissions } = usePermission();

        return {
            dualViewSetting,
            clearDualViewRef,
            updateDualViewRef,
            permissions,
        };
    },
    data() {
        return {
        };
    },
    computed: {
        hasDensity() {
            return pluginsMapByName.get(this.selfName).enableDensity;
        },
        selfHandelPadding() {
            return pluginsMapByName.get(this.selfName).selfHandelPadding;
        },
        dualViewClassStatus() {
            return {
                'dual-aside-box--no-padding': this.selfHandelPadding,
                'dual-aside-box--no-density': !this.hasDensity,
                'dual-aside-box--has-catalog': this.selfName === 'desc',
            };
        },
        defaultTabs() {
            const sideDisplayed = getDualViewDisplayTabBySide(this.dualViewSetting, this.side);
            const sideConfigNames = (_.get(this.dualViewSetting, this.side) || []).map(item => item.name);
            const filteredAclList = getFilteredManifestList(this.side, this.$store)
                .map(mf => (
                    {
                        label: this.$t(mf.label),
                        key: mf.name,
                        hasAcl: !mf.aclCheckKey || (mf.aclCheckKey && this.permissions[mf.aclCheckKey]),
                        isDisplay: sideDisplayed.includes(mf.name) || !sideConfigNames.includes(mf.name),
                    }
                ))
                .filter(item => item.hasAcl && item.isDisplay && (item.key !== 'kwic' || (window.self !== window.top && !!this.$route.query.q)));
            return _.sortBy(filteredAclList, ({ key }) => sideDisplayed.findIndex(name => key === name));
        },
    },
    watch: {
        selfName: {
            handler(newVal) {
                this.checkCurrentTabIsExist(newVal);
            },
            immediate: true,
        },
        dualViewSetting: {
            handler() {
                this.checkCurrentTabIsExist(this.selfName);
                this.$nextTick(() => {
                    this.resetTabsStatus();
                });
            },
            deep: true,
        },
    },
    updated() {
        this.$nextTick(() => {
            this.updateDualViewRef({ side: this.side, ref: this.$refs.viewVm });
        });
    },
    destroyed() {
        this.clearDualViewRef({ side: this.side });
    },
    methods: {
        switchTabHandler(tab) {
            this.$router.push({
                name: this.$route.name,
                params: {
                    ...this.$route.params,
                    [this.side]: tab.key,
                },
                query: this.$route.query,
            });
            const routeNames = getRouteNameList(this.$route);
            window.rvTrack('Toggle Dual View Tab', { left: routeNames[0], right: routeNames[1] });
        },
        resetTabsStatus() {
            const menuTabsScrollableReset = _.get(this.$refs, 'menuTabsScrollable.initScrollStatus');
            menuTabsScrollableReset && menuTabsScrollableReset();
        },
        resizeHandler() {
            this.resetTabsStatus();
        },
        checkCurrentTabIsExist(currentTab) {
            if (_.findIndex(this.defaultTabs, { key: currentTab }) < 0) { // 404逻辑
                console.error(`404 Unknown route ${currentTab}, redirect`);
                this.$router.replace({
                    params: {
                        [this.side]: this.defaultTabs[0].key,
                    },
                    query: this.$route.query,
                });
            }
            this.$nextTick(() => {
                this.updateDualViewRef({ side: this.side, ref: this.$refs.viewVm });
            });
        },
    },
};
</script>

<style lang="scss">
.ps-side-view {
    position: relative;
    width: 50%;
    .ps-side-view__tab {
        background-color: $bg-normal;
    }
    .density-bar{
        bottom: 0;
        right: 18px;
    }
    &.view-left {
        border-right: 1px solid #DFE1E6;
    }
}
.dual-aside-box{
    overflow: auto;
    max-height: 100%;
    padding: 16px 40px 16px 16px;
    position: relative;
    &--no-density {
        padding: 16px;
    }
    &--no-padding {
        padding: 0;
    }
    &--has-catalog {
        padding-right: 56px;
    }
}
</style>
